import React, { useRef, useState } from 'react';
import { RotateCwSquare, RotateCcwSquare, ArrowUpFromLine, ArrowDownFromLine, LoaderCircle, Maximize, Minimize, Plus, Minus, Compass } from 'lucide-react';

export const NavbarButton = ({ children, tooltipText, onClick, isActive = false, isLoading = false, disabled = false, onHover, onLeave, containerRef }) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      aria-label={tooltipText}
      disabled={disabled || isLoading}
      onMouseEnter={() => {
        const parentRect = containerRef?.current?.getBoundingClientRect();
        if (!parentRect) return;
        onHover({ text: tooltipText, x: parentRect.left + parentRect.width / 2, y: parentRect.top - 30 });
      }}
      onMouseLeave={onLeave}
      className={`p-1.5 rounded transition-colors ${
        isActive
          ? 'bg-red-600 text-white'
          : 'bg-white text-gray-600 hover:bg-gray-200 active:bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {isLoading ? (
        <LoaderCircle className="h-4 w-4 animate-spin" />
      ) : (
        children
      )}
    </button>
  );
};

export default function MapCameraControls({ adjustMap, zoomIn, zoomOut, resetHeading, onToggleFullscreen, isFullscreen, onPointerEnter }) {
  const compassRef = useRef(null);
  const [localTooltip, setLocalTooltip] = useState(null);
  return <div onPointerEnter={onPointerEnter}>
      {/* Navigation compass - bottom right */}
      <div
        className="absolute bottom-3 right-3 z-[1001]"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <div ref={compassRef} className="bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 flex flex-col items-center gap-1">
          <div className="flex items-center gap-0">
            <NavbarButton tooltipText="Rotate Left" onClick={() => adjustMap("rotate", 15)} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
              <RotateCcwSquare className="h-4 w-4" />
            </NavbarButton>
            <div className="flex flex-col items-center gap-2">
              <NavbarButton tooltipText="Tilt Up" onClick={() => adjustMap("tilt", -15)} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
                <ArrowUpFromLine className="h-4 w-4" />
              </NavbarButton>
              <NavbarButton tooltipText="Tilt Down" onClick={() => adjustMap("tilt", 15)} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
                <ArrowDownFromLine className="h-4 w-4" />
              </NavbarButton>
            </div>
            <NavbarButton tooltipText="Rotate Right" onClick={() => adjustMap("rotate", -15)} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
              <RotateCwSquare className="h-4 w-4" />
            </NavbarButton>
          </div>
          <div className="w-full h-px bg-gray-200" />
          <div className="flex items-center gap-1">
            <NavbarButton tooltipText="Zoom In" onClick={zoomIn} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
              <Plus className="h-4 w-4" />
            </NavbarButton>
            <NavbarButton tooltipText="Zoom Out" onClick={zoomOut} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
              <Minus className="h-4 w-4" />
            </NavbarButton>
          </div>
          <div className="w-full h-px bg-gray-200" />
          <div className="flex items-center gap-1">
            <NavbarButton tooltipText="Reset View" onClick={resetHeading} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
              <Compass className="h-4 w-4" />
            </NavbarButton>
            <NavbarButton tooltipText={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} onClick={onToggleFullscreen} isActive={isFullscreen} onHover={setLocalTooltip} onLeave={() => setLocalTooltip(null)} containerRef={compassRef}>
              {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </NavbarButton>
          </div>
        </div>
      </div>

    {localTooltip && <div className="fixed bg-gray-900 text-white text-xs rounded-md px-2 py-1 shadow-lg z-[2002] pointer-events-none whitespace-nowrap" style={{ top: localTooltip.y, left: localTooltip.x, transform: 'translateX(-50%)' }}>{localTooltip.text}</div>}
  </div>;
}
